import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import './TooltipSelect.css';

interface SelectOption {
  value: string;
  label: string;
  tooltip?: string;
}

interface TooltipSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  placeholderTooltip?: string;
  className?: string;
  disabled?: boolean;
}

const TooltipSelect: React.FC<TooltipSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select',
  placeholderTooltip,
  className = '',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<'right' | 'top'>('right');
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Check tooltip position and adjust if it would be cut off
  useEffect(() => {
    if (hoveredOption && tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // If tooltip bottom extends past viewport, position it above
      if (rect.bottom > viewportHeight - 20) {
        setTooltipPosition('top');
      } else {
        setTooltipPosition('right');
      }
    }
  }, [hoveredOption]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);
  const displayLabel = selectedOption ? selectedOption.label : placeholder;

  // Determine if placeholder should be disabled (when a real option is selected)
  const hasSelection = value !== '' && value !== undefined;

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div 
      className={`tooltip-select-container ${className} ${disabled ? 'disabled' : ''}`} 
      ref={containerRef}
    >
      <div 
        className={`tooltip-select-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            !disabled && setIsOpen(!isOpen);
          }
        }}
      >
        <span className={`tooltip-select-value ${!selectedOption ? 'placeholder' : ''}`}>
          {displayLabel}
        </span>
        <ChevronDown className={`tooltip-select-arrow ${isOpen ? 'open' : ''}`} size={18} />
      </div>

      {isOpen && (
        <div className="tooltip-select-dropdown">
          {/* Placeholder option - disabled if a selection has been made */}
          <div
            className={`tooltip-select-option placeholder-option ${hasSelection ? 'disabled' : ''}`}
            onClick={() => !hasSelection && handleOptionClick('')}
            onMouseEnter={() => setHoveredOption('placeholder')}
            onMouseLeave={() => setHoveredOption(null)}
          >
            <span>{placeholder}</span>
            {placeholderTooltip && hoveredOption === 'placeholder' && !hasSelection && (
              <div 
                ref={tooltipRef}
                className={`tooltip-select-tooltip ${tooltipPosition === 'top' ? 'tooltip-top' : ''}`}
              >
                {placeholderTooltip}
              </div>
            )}
          </div>

          {/* Regular options */}
          {options.map((option) => (
            <div
              key={option.value}
              className={`tooltip-select-option ${value === option.value ? 'selected' : ''}`}
              onClick={() => handleOptionClick(option.value)}
              onMouseEnter={() => setHoveredOption(option.value)}
              onMouseLeave={() => setHoveredOption(null)}
            >
              <span>{option.label}</span>
              {option.tooltip && hoveredOption === option.value && (
                <div 
                  ref={tooltipRef}
                  className={`tooltip-select-tooltip ${tooltipPosition === 'top' ? 'tooltip-top' : ''}`}
                >
                  {option.tooltip}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TooltipSelect;
